import {FormControl, FormField, useFormContext} from 'platform/components';
import {Box, Hide, HStack, Integer, Show, Spinner} from 'platform/foundation';

import {useCallback, useEffect, useRef} from 'react';
import {FieldValues, Path} from 'react-hook-form';

import {useLazyCalculateFromWithoutVatQuery, useLazyCalculateFromWithVatQuery} from '@dms/api/core';
import {PriceWithVatDirectionEnum} from '@dms/api/finance';
import {CountryAlpha3CodeEnum, VatCalculationResponseBody, VatRateType} from '@dms/api/shared';
import i18n from '@dms/i18n';

import {
  CurrencyCodeType,
  NonNullableFieldPathByValue,
  RequiredTestIdProps,
  suffixTestId,
  useDebouncedCallback,
} from 'shared/web';

import {useFormValues} from '../../../hooks/useFormValues';
import {useGetVatRatesOptions} from '../../../hooks/useGetVatRatesOptions';
import {handleApiError} from '../../../utils/handleApiError';
import {CoercedMoney, VatDirection} from '../VehicleTransactionFormSchema';

type VatPriceStructure = {
  withVat: CoercedMoney;
  withoutVat: CoercedMoney;
  vatDirection?: VatDirection | null;
  vatRate?: string | null;
  vatType?: string | null;
  isCalculatingVat?: boolean;
};

const INPUT_HEIGHT: Integer = 15;
const DEBOUNCE_WAIT = 500;

interface FormVatPriceFieldProps<T extends FieldValues> extends RequiredTestIdProps {
  name: NonNullableFieldPathByValue<T, VatPriceStructure>;
  control: FormControl<T>;
  currency: CurrencyCodeType;
  countryCode: CountryAlpha3CodeEnum;
  isDisabled?: boolean;
  labels: {
    withVat: string;
    withoutVat: string;
  };
  tooltips?: {
    withVat?: string;
    withoutVat?: string;
    vatType?: string;
  };
}

export function FormVatPriceField<T extends FieldValues = FieldValues>(
  props: FormVatPriceFieldProps<T>
) {
  const [vatRates] = useGetVatRatesOptions();

  const [calculatePriceWithVat] = useLazyCalculateFromWithVatQuery();
  const [calculatePriceWithoutVat] = useLazyCalculateFromWithoutVatQuery();

  const {setValues, setValue} = useFormContext<{vatCode: string; isCalculatingVat?: boolean}>();

  const {[props.name]: vatFields} = useFormValues<T>(props.name);

  const {vatRate, withoutVat, vatType} = (vatFields as VatPriceStructure) ?? {};

  const {name: vatFieldsName} = props;

  const debouncedCalculatePriceWithoutVat = useDebouncedCallback(
    (value: number | string, onResponse: (response: VatCalculationResponseBody) => void) => {
      calculatePriceWithoutVat({
        amount: typeof value === 'number' ? value?.toString() : (value ?? ''),
        vatRateType: vatType as VatRateType,
        countryCode: props.countryCode,
      })
        .unwrap()
        .then(onResponse)
        .catch(handleApiError);
    },
    DEBOUNCE_WAIT
  );

  const debouncedCalculatePriceWithVat = useDebouncedCallback(
    (value: number | string, onResponse: (response: VatCalculationResponseBody) => void) => {
      calculatePriceWithVat({
        amount: typeof value === 'number' ? value?.toString() : (value ?? ''),
        vatRateType: vatType as VatRateType,
        countryCode: props.countryCode,
      })
        .unwrap()
        .then(onResponse)
        .catch(handleApiError);
    },
    DEBOUNCE_WAIT
  );

  const buildFieldName = useCallback(
    (fieldName: string, suffix?: string) =>
      `${vatFieldsName as string}.${fieldName}${suffix ? `.${suffix}` : ''}` as Path<T>,
    [vatFieldsName]
  );

  const buildPriceAmountFieldName = useCallback(
    (fieldName: 'withVat' | 'withoutVat') => buildFieldName(fieldName, 'amount'),
    [buildFieldName]
  );

  const dynamicDefaultValuesSet = useRef(false);
  const {currency} = props;

  useEffect(
    function applyDynamicDefaultVatValues() {
      if (
        dynamicDefaultValuesSet.current === false &&
        vatRates.length > 0 &&
        vatType === undefined
      ) {
        // needs to be delayed to avoid "setValue" being called before the form is fully rendered
        setTimeout(() => {
          setValues({
            [buildFieldName('vatRate') as Path<T>]: vatRates[0].rate,
            [buildFieldName('vatType')]: vatRates[0].value,
            [buildFieldName('vatDirection')]: PriceWithVatDirectionEnum.FROM_PRICE_WITHOUT_VAT,
            [buildFieldName('withVat', 'currency')]: currency,
            [buildFieldName('withoutVat', 'currency')]: currency,
          } as Partial<T>);
        });
        dynamicDefaultValuesSet.current = true;
      }
      if (
        dynamicDefaultValuesSet.current === false &&
        vatRates.length > 0 &&
        vatType !== undefined &&
        vatRate === undefined
      ) {
        setTimeout(() => {
          setValues({
            [buildFieldName('vatRate') as Path<T>]: vatRates.find((rate) => rate.value === vatType)
              ?.rate,
          } as Partial<T>);
        });
        dynamicDefaultValuesSet.current = true;
      }
    },
    [vatRates, vatType, setValues, buildFieldName, currency, vatRate]
  );

  return (
    <>
      <Show when={vatRates.length > 0}>
        <HStack align="center" spacing={4} width="100%" wrap>
          <Box flexGrow={1} flexShrink={0} minWidth="40%" height={INPUT_HEIGHT}>
            <FormField
              data-testid={suffixTestId('withoutVatAmount', props)}
              type="currency"
              name={buildPriceAmountFieldName('withoutVat')}
              label={props.labels.withoutVat}
              isDisabled={props.isDisabled}
              control={props.control}
              decimalPlaces={2}
              tooltip={props.tooltips?.withoutVat}
              onChange={(value) => {
                if (value) {
                  setValue(buildFieldName('isCalculatingVat') as 'isCalculatingVat', true);
                  debouncedCalculatePriceWithoutVat(value, (res) => {
                    setValues({
                      [buildPriceAmountFieldName('withVat')]: res.withVat,
                      [buildFieldName('vatDirection')]:
                        PriceWithVatDirectionEnum.FROM_PRICE_WITHOUT_VAT,
                      [buildFieldName('isCalculatingVat')]: false,
                    } as Partial<T>);
                  });
                }
              }}
            />
          </Box>
          <Box flexShrink={0} flexGrow={0} flexBasis={20} height={INPUT_HEIGHT}>
            <FormField
              data-testid={suffixTestId('vatType', props)}
              type="choice"
              name={buildFieldName('vatType')}
              options={vatRates}
              control={props.control}
              label={i18n.t('general.labels.vat')}
              isNotClearable
              menuInPortal
              isDisabled={props.isDisabled}
              tooltip={props.tooltips?.vatType}
              onChange={(value) => {
                if (value && withoutVat?.amount) {
                  setValue(buildFieldName('isCalculatingVat') as 'isCalculatingVat', true);
                  calculatePriceWithoutVat({
                    amount: withoutVat?.amount,
                    vatRateType: value as VatRateType,
                    countryCode: props.countryCode,
                  })
                    .unwrap()
                    .then((res) => {
                      setValues({
                        [buildPriceAmountFieldName('withVat')]: res.withVat,
                        [buildPriceAmountFieldName('withoutVat')]: res.withoutVat,
                        [buildFieldName('vatRate')]: vatRates.find((rate) => rate.value === value)
                          ?.rate,
                        [buildFieldName('isCalculatingVat')]: false,
                      } as Partial<T>);
                    })
                    .catch(handleApiError);
                }
              }}
            />
          </Box>
          <Box flexGrow={3} flexShrink={0} height={INPUT_HEIGHT}>
            <FormField
              data-testid={suffixTestId('withVatAmount', props)}
              type="currency"
              label={props.labels.withVat}
              name={buildPriceAmountFieldName('withVat')}
              control={props.control}
              isDisabled={props.isDisabled}
              decimalPlaces={2}
              tooltip={props.tooltips?.withVat}
              onChange={(value) => {
                if (value) {
                  setValue(buildFieldName('isCalculatingVat') as 'isCalculatingVat', true);
                  debouncedCalculatePriceWithVat(value, (res) => {
                    setValues({
                      [buildPriceAmountFieldName('withoutVat')]: res.withoutVat,
                      [buildFieldName('vatDirection')]:
                        PriceWithVatDirectionEnum.FROM_PRICE_WITH_VAT,
                      [buildFieldName('isCalculatingVat')]: false,
                    } as Partial<T>);
                  });
                }
              }}
            />
          </Box>
        </HStack>
      </Show>
      <Hide when={vatRates.length > 0}>
        <Spinner />
      </Hide>
    </>
  );
}
